import React, { useEffect, useRef, useState, type FC } from "react";
import { useProductSearchQuery } from "../../ProductSearchContextProvider";
import useAutocomplete from "../../hooks/useAutocomplete";
import { useBeforeTypingProducts } from "../../hooks/useBeforeTypingProducts";
import { useProductViewStorage } from "@/hooks/useProductViewStorage";
import useAlgoliaAutocomplete from "../../hooks/useAlgoliaAutocomplete";

// File with test products to populate the states while the data fetch is worked on - removed when fetching is done
import { recentlyViewedItems, featuredItems } from "./testproduct";
import { ProductSearchListItemProps } from "./ProductSearchListItem";
import { Category } from "../../hooks/useAlgoliaAutocomplete";

import DefaultState from "./DefaultState";
import AfterState from "./AfterState";
import NoResults from "./NoResultsState";
import ErrorState from "./ErrorState";

enum State {
  BEFORE = "BEFORE", // Before user types State
  AFTER = "AFTER", // After user types state
  NO_RESULTS = "NO_RESULTS", // When results come back
  ERROR = "ERROR", // When an error occurs when searching
}
interface StateComponentProps {
  recentlyViewed: ProductSearchListItemProps[];
  featuredItems: ProductSearchListItemProps[];
  categories: Category[];
  resultItems: ProductSearchListItemProps[];
  query: string;
  categoryLinks?: {
    label: string;
    url: string;
  }[];
  location?: string;
}

const UIStateMap: Record<State, FC<StateComponentProps>> = {
  [State.BEFORE]: (props) => (
    <DefaultState
      recentlyViewed={props.recentlyViewed}
      featuredItems={props.featuredItems}
      location={props.location}
    />
  ),
  [State.AFTER]: (props) => (
    <AfterState
      categories={props.categories}
      resultItems={props.resultItems}
      query={props.query}
      location={props.location}
    />
  ),
  [State.NO_RESULTS]: (props) => (
    <NoResults location={props.location} categoryLinks={props.categoryLinks} />
  ),
  [State.ERROR]: (props) => (
    <ErrorState
      errorCallback={() => console.log("errorCallback")}
      location={props.location}
    />
  ),
};

interface Props {
  featuredPrograms?: string[];
  featuredCategories?: {
    label: string;
    url: string;
  }[];
  location?: string;
}

const ProductSuggestions: FC<Props> = (props) => {
  // state management
  const [uiState, setUiState] = useState(State.BEFORE);
  const [isLoading, setIsLoading] = useState(false);
  const prevQueryRef = useRef<string>("");

  // Fetch recently viewed product UUIDs from local storage here and replace.
  const { getProductViews } = useProductViewStorage();
  const recentlyViewedUUIDs: string[] = getProductViews().map(
    (product) => product.courseId ?? product.productId,
  );

  const {
    featuredPrograms: featuredProgramsUUIDs,
    featuredCategories = [
      { label: "Business", url: "/search?subject=Business" },
      { label: "Computer Science", url: "/search?subject=Computer%20Science" },
      { label: "Data Science", url: "/search?subject=Data%20Science" },
    ],
  } = props;

  const {
    recentlyViewed,
    featuredPrograms,
    isLoading: beforeTypingIsLoading,
    error,
  } = useBeforeTypingProducts(
    recentlyViewedUUIDs,
    props.featuredPrograms ?? [],
  );

  // data fetch
  const { query } = useProductSearchQuery();
  const results = useAlgoliaAutocomplete(query);

  useEffect(() => {
    // Check for Algolia error
    if (results.hasError) {
      setUiState(State.ERROR);
      setIsLoading(false);
      return;
    }
    // Before typing or query cleared - show default state
    if (!query || query.trim().length === 0) {
      setUiState(State.BEFORE);
      setIsLoading(false);
      prevQueryRef.current = "";
      return;
    }

    // Query changed - set loading
    if (query !== prevQueryRef.current) {
      prevQueryRef.current = query;
      setIsLoading(true);
    }

    // User has typed something and results have come back
    if (query.trim().length > 0) {
      setIsLoading(false);

      if (results?.products.length > 0) {
        setUiState(State.AFTER);
      } else {
        setUiState(State.NO_RESULTS);
      }
    }
  }, [query, results]);

  // current state
  const Component: FC<StateComponentProps> = UIStateMap[uiState];

  // Pass what the screens need
  // Build stateProps with all data needed by child components
  const stateProps: StateComponentProps = {
    recentlyViewed: recentlyViewed, // Replace with actual fetched data - these come from test items file
    featuredItems: featuredPrograms, // Replace with actual fetched data - these come from test items file
    categories: results.categories,
    resultItems: results.products,
    query: query || "",
    categoryLinks: featuredCategories,
    location: props.location,
  };

  return (
    <div className="w-full">
      <Component {...stateProps} />
    </div>
  );
};

export default ProductSuggestions;
